import { useEffect, useMemo } from "react";

import { sub } from "date-fns";
import { useLocation } from "react-router-dom";

import { getReport } from "@/api/endpoints/reports";
import { METRIC, Metric } from "@/constants";
import MentionsModalURL from "@/containers/MentionsModalURL";
import {
  MENTIONS_MODAL_OPEN_PARAM,
  MODAL_FILTER_PREFIX,
} from "@/containers/MentionsModalURL/settings";
import { useMentionCards } from "@/contexts/MentionCardsContext";
import useFilterParams from "@/hooks/useFilterParams";
import useSearchParams from "@/hooks/useSearchParams";
import { SearchFilter } from "@/types/SearchFilter";
import {
  formatDateForApi,
  highlightElement,
  newDateWithCurrentTimezone,
  prefixKeys,
} from "@/utils";
import { METRIC_WIDGET_MAP } from "@/views/reports/widgets/settings";
import { Widget } from "@/views/reports/widgets/Widget";
import Container from "@components/Container";
import ErrorComponent from "@components/Error";
import WidgetGrid, {
  full,
  GridItem,
  GridSize,
  half,
  INSIGHTS_WIDGET_SIZES,
} from "@components/WidgetGrid";
import { useQuery } from "@tanstack/react-query";

// Override the default full size for the PPT layout
// - Pie widgets should render at 50% width
const PPT_SIZES: Partial<Record<Metric, GridSize>> = {
  [METRIC.INSIGHTS_SENTIMENT]: half,
  [METRIC.INSIGHTS_NUM_MENTIONS_PER_MEDIA_CATEGORY]: half,
};

function getInsightsGridSize(
  metric: Metric,
  ppt: boolean,
  showSocialMedia: boolean
): GridSize {
  if (ppt) return PPT_SIZES[metric] || full;
  if (metric === METRIC.INSIGHTS_REACH_OVER_TIME_AUDIO && !showSocialMedia) {
    return full;
  }
  return INSIGHTS_WIDGET_SIZES[metric] || full;
}

const MentionsInsightsView = () => {
  const { alert } = useMentionCards();
  const location = useLocation();

  const [filters] = useFilterParams();

  // Handle hash in URL on page load and refresh
  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const hash = location.hash.substring(1);
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          highlightElement(element);
        }
      }, 1000); // Allow time for data to load and render
    }
  }, [location.hash, alert?.insights_report_uuid]);

  const [params, setParams] = useSearchParams<
    { ppt?: boolean } & SearchFilter & { [MENTIONS_MODAL_OPEN_PARAM]: string }
  >();

  const { startDate, endDate } = useMemo(
    () => ({
      startDate: filters.start_date
        ? filters.start_date
        : formatDateForApi(sub(new Date(), { months: 3 })),
      endDate: formatDateForApi(
        filters.end_date
          ? newDateWithCurrentTimezone(filters.end_date)
          : new Date()
      ),
    }),
    [filters.start_date, filters.end_date]
  );

  const reportUuid = alert?.insights_report_uuid;

  const { data: reportData, error } = useQuery({
    queryKey: ["reports", reportUuid],
    enabled: Boolean(reportUuid),
    queryFn: () => {
      if (!reportUuid) {
        throw new Error();
      }

      return getReport(reportUuid);
    },
  });
  if (!reportData?.ok) return null;
  const report = reportData.data;

  const isError = report && "detail" in report;

  return (
    <Container fullWidth data-testid="insights-page">
      {report ? (
        isError ? (
          <ErrorComponent error={report.detail} />
        ) : (
          <WidgetGrid>
            {report.widgets
              .filter((widget) => widget.metric in METRIC_WIDGET_MAP)
              .map((widget) => (
                <GridItem
                  size={getInsightsGridSize(
                    widget.metric,
                    params["ppt"] === true,
                    Boolean(report.show_socialmedia_widget)
                  )}
                  key={widget.uuid}
                  data-testid={widget.metric}
                  id={widget.metric}
                >
                  <Widget
                    size={null}
                    {...widget}
                    dontSize
                    reportUuid={report.uuid}
                    reportTemplate={report.template}
                    interval={report.interval}
                    startDate={startDate}
                    endDate={endDate}
                    filters={filters}
                    searchId={widget.searches[0]?.uuid}
                    fetchMentions_v2={(widgetSpecificFilters: SearchFilter) => {
                      // Prefix all keys with the modal specific settings
                      const f = prefixKeys(MODAL_FILTER_PREFIX, {
                        searches: widget.searches.map((search) => search.uuid),
                        report: report?.uuid,
                        ...widgetSpecificFilters,
                      });
                      // Update the query params to trigger a new fetch
                      setParams({
                        ...f,
                        [MENTIONS_MODAL_OPEN_PARAM]: widget.metric,
                      });
                    }}
                    hasAudioSources={report?.has_audio_sources}
                  />
                </GridItem>
              ))}
          </WidgetGrid>
        )
      ) : error ? (
        <ErrorComponent />
      ) : null}
      {/* Modal to show mentions when clicking in the charts */}
      <MentionsModalURL />
    </Container>
  );
};

export default MentionsInsightsView;
